<template>
  <section :class="{ 'sp-sched--compact': compact }" class="sp-sched">
    <div class="sp-sched__head">
      <span class="sp-sched__title">{{ schedule.title }}</span>
      <span v-if="schedule.summary" class="sp-sched__sum">{{ schedule.summary }}</span>
    </div>

    <div v-if="grid && !compact" :style="{ '--sp-tt-cols': grid.columns.length }" class="sp-wk">
      <div v-for="column in grid.columns" :key="`c${column.day}`" :class="{ 'sp-wk__col--today': column.day === today }" class="sp-wk__col">
        <span class="sp-wk__day">{{ column.label }}</span>
        <a v-for="(block, i) in sortedBlocks(column.blocks)" :key="`${block.courseId}-${i}`" :aria-label="block.tooltip"
           :class="block.tone" :href="courseHref(block.courseId)" :title="block.tooltip" class="sp-wk__chip"
           @click.prevent="openCourse(block.courseId, $event)">
          <span class="sp-wk__time">{{ timeRange(block) }}</span>
          <span class="sp-wk__label">{{ block.label }}</span>
        </a>
        <span v-if="!column.blocks.length" class="sp-wk__none">–</span>
      </div>
    </div>

    <div v-if="schedule.legend.length && !compact" class="sp-tt__legend">
      <span v-for="item in schedule.legend" :key="item.label" class="sp-tt__key">
        <span :class="item.tone" class="sp-sched__dot"/>{{ item.label }}
      </span>
    </div>

    <div v-if="isEmpty" class="sp-sched__empty">Keine aktiven Kurse</div>
    <div v-for="(section, s) in visibleSections" :key="`s${s}`" class="sp-sched__sec">
      <div v-if="section.label" class="sp-sched__sec-head">
        <span v-if="section.tone" :class="section.tone" class="sp-sched__dot"/>
        <a v-if="section.to" :href="hrefOf(section.to)" @click.prevent="go(section.to, $event)">{{ section.label }}</a>
        <span v-else>{{ section.label }}</span>
        <span v-if="section.hint" class="tag tag--sm tone-muted">{{ section.hint }}</span>
      </div>
      <a v-for="row in section.rows" :key="row.courseId" :href="courseHref(row.courseId)" class="sp-sched__row"
         @click.prevent="openCourse(row.courseId, $event)">
        <span :class="row.tone" class="sp-sched__bar"/>
        <span class="sp-sched__when">{{ section.byDay ? row.time : row.when }}</span>
        <span class="sp-sched__main">
          <span class="sp-sched__name">{{ row.title }}</span>
          <span v-if="row.details.length" class="sp-sched__sub">{{ row.details.join(' · ') }}</span>
          <span v-if="row.seats" :class="row.seatsTone" class="tag tag--sm sp-sched__seats">{{ row.seats }}</span>
        </span>
      </a>
      <div v-if="!section.rows.length" class="sp-sched__empty">Keine aktiven Kurse</div>
    </div>
  </section>
</template>

<script lang="ts">
import Vue, {PropType} from 'vue';
import moment from 'moment';
import {RawLocation} from 'vue-router';
import {buildTimetableGrid, GridBlock, QuickSchedule, ScheduleSection, TimetableGrid} from '@/components/app/search/searchSchedule';
import {routeOfEntity} from '@/components/app/search/searchEntities';

/** Courses of the quick view: compact week strip (desktop) and course list per child / weekday */
export default Vue.extend({
  props: {
    schedule: {type: Object as PropType<QuickSchedule>, required: true},
    /** Mobile compact quick view: list only, no grid */
    compact: {type: Boolean, default: false},
  },
  computed: {
    grid(): TimetableGrid | null {
      return buildTimetableGrid(this.schedule.blocks);
    },
    today(): number {
      return moment().isoWeekday() - 1;
    },
    isEmpty(): boolean {
      return !this.schedule.sections.some((section) => section.rows.length || section.label);
    },
    visibleSections(): ScheduleSection[] {
      return this.schedule.sections.filter((section) => section.rows.length || section.label);
    },
  },
  methods: {
    sortedBlocks(blocks: GridBlock[]): GridBlock[] {
      return [...blocks].sort((a, b) => a.start - b.start || a.end - b.end);
    },
    timeRange(block: GridBlock): string {
      const fmt = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
      return `${fmt(block.start)}–${fmt(block.end)}`;
    },
    hrefOf(to: RawLocation): string {
      return this.$router.resolve(to).href;
    },
    courseHref(id: string): string {
      const route = routeOfEntity('courses', id);
      return route ? this.hrefOf(route) : '#';
    },
    go(to: RawLocation, event: MouseEvent) {
      this.$emit('navigate', to, event.metaKey || event.ctrlKey);
    },
    openCourse(id: string, event: MouseEvent) {
      const route = routeOfEntity('courses', id);
      if (route) {
        this.go(route, event);
      }
    },
  },
});
</script>

<style src="./searchTimetable.css"></style>
